"use client";

import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  EntitySetupDialog,
  mapWorkplaceFieldFromApi,
  type AppCrmMapView,
  type EntityCrmMapEditorLoadResult,
  type EntitySetupDialogMode,
  type EntitySetupEventType,
  type EntitySetupMapSaveInput,
} from "skedyul-ui/ui";
import { trpc } from "@utils/api";
import type { RootState } from "@store/index";
import { store } from "@store/index";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { refreshInstallPanels } from "@store/features/apps/appResourcePageSlice";
import { consumeEntitySetupManualCallback } from "@/lib/openEntitySetupSidebar";

export type EntitySetupSidebarParams = {
  installId: string
  title: string
  description?: string
  mode?: EntitySetupDialogMode
  entityHandles: string[]
  eventTypes: EntitySetupEventType[]
  workflowHandles: string[]
  showOtherWorkflows?: boolean
  recommendedWorkflowHandle?: string
  successToast?: string
  /** When true, show full field editor with direction controls after model selection */
  showFullFieldEditor?: boolean
}

export const EntitySetupSidebarHost: React.FC = () => {
  const params = useSelector(
    (state: RootState) =>
      state.sideNavigation.right.params as EntitySetupSidebarParams,
  )

  const close = useCallback(() => {
    store.dispatch(closeSideNavigation({ position: "right" }))
  }, [])

  if (!params?.installId) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-sm text-muted-foreground">
        Setup details unavailable.
      </div>
    )
  }

  const installId = params.installId

  return (
    <EntitySetupDialog
      key={`entity-setup-${installId}-${params.mode ?? "full"}-${(params.entityHandles ?? []).join(",")}-${(params.workflowHandles ?? []).join(",")}`}
      open
      onOpenChange={(open) => {
        if (!open) close()
      }}
      title={params.title}
      description={params.description}
      mode={params.mode ?? "full"}
      entityHandles={params.entityHandles ?? []}
      eventTypes={params.eventTypes ?? []}
      recommendedWorkflowHandle={params.recommendedWorkflowHandle}
      workflowHandles={params.workflowHandles ?? []}
      showOtherWorkflows={params.showOtherWorkflows ?? true}
      showFullFieldEditor={params.showFullFieldEditor ?? false}
      fetchModels={async () => {
        const models = await trpc.crm.getModels.query()
        return (models ?? []).map((m) => {
          const row = m as { id: string; handle: string; name: string }
          return {
            id: row.id,
            handle: row.handle,
            name: row.name,
          }
        })
      }}
      fetchCrmSetup={async (entityHandles, modelSelections) => {
        return trpc.apps.getCrmInstallSetup.query({
          appInstallationId: installId,
          entityHandles,
          modelSelections,
        })
      }}
      applyConnections={async (entityHandles, modelSelections, fieldDefaults) => {
        return trpc.apps.applyCrmInstallSetup.mutate({
          appInstallationId: installId,
          entityHandles,
          modelSelections,
          fieldDefaults,
        })
      }}
      onApproveMigration={async (migrationId) => {
        await trpc.crm.approveMigration.mutate({ migrationId })
      }}
      onDenyMigration={async (migrationId) => {
        await trpc.crm.denyMigration.mutate({ migrationId })
      }}
      loadMap={async (entityHandle): Promise<EntityCrmMapEditorLoadResult> => {
        const result = await trpc.apps.getAppCrmMap.query({
          appInstallationId: installId,
          entityHandle,
          includeDefaults: true,
        })
        const suggestedDefaults: AppCrmMapView | null = result.suggestedDefaults
          ? {
              entityHandle,
              targetModelId: result.suggestedDefaults.targetModelId,
              matchFieldId: result.suggestedDefaults.matchFieldId,
              matchRules: result.suggestedDefaults.matchRules,
              fieldMappings: result.suggestedDefaults.fieldMappings.map(
                (mapping) => ({
                  entityPath: mapping.entityPath,
                  targetFieldId: mapping.targetFieldId,
                  mode: mapping.mode,
                  sourceLiquid: mapping.sourceLiquid,
                  valueMap: mapping.valueMap,
                  defaultValue: mapping.defaultValue,
                }),
              ),
              relationshipMappings:
                result.suggestedDefaults.relationshipMappings,
            }
          : null
        return {
          map: result.map,
          entity: result.entity,
          suggestedDefaults,
        }
      }}
      saveMap={async (input: EntitySetupMapSaveInput) => {
        await trpc.apps.upsertAppCrmMap.mutate({
          appInstallationId: installId,
          entityHandle: input.entityHandle,
          targetModelId: input.targetModelId,
          matchFieldId: input.matchFieldId,
          matchRules: input.matchRules,
          fieldMappings: input.fieldMappings,
          relationshipMappings: input.relationshipMappings,
        })
      }}
      fetchFields={async (modelId) => {
        const model = await trpc.crm.getModel.query({
          modelId,
          includes: {
            fields: {
              definition: { options: true },
            },
          },
        })
        const fields =
          (
            model as {
              fields?: Array<{
                id: string
                handle: string
                name: string
                label?: string
                type?: string
                isUnique?: boolean
                definition?: {
                  options?: Array<{ value: string; label: string }>
                } | null
              }>
            }
          )?.fields ?? []
        return fields.map((f) => mapWorkplaceFieldFromApi(f))
      }}
      provisionWorkflows={async (workflowHandles) => {
        await trpc.apps.provisionAppRealtimeWorkflows.mutate({
          appInstallationId: installId,
          workflowHandles,
        })
      }}
      listCompatibleWorkflows={async (eventType) => {
        return trpc.apps.listTypeMatchedInstallWorkflows.query({
          appInstallationId: installId,
          eventType,
        })
      }}
      fetchLiveUpdatesOverview={async (
        eventTypes,
        recommendedWorkflowHandle,
      ) => {
        return trpc.apps.getEventWiringOverview.query({
          appInstallationId: installId,
          eventTypes,
          recommendedWorkflowHandle,
        })
      }}
      onFinish={async (input) => {
        if (input.enableLiveUpdates) {
          await trpc.apps.wireInstallEventSubscriptions.mutate({
            appInstallationId: installId,
            selections: input.selections,
          })
        } else if (input.connectionsApplied) {
          await trpc.apps.reconcileAppInstallSetupSteps.mutate({
            appInstallationId: installId,
          })
        } else {
          await trpc.apps.reconcileAppInstallSetupSteps.mutate({
            appInstallationId: installId,
          })
        }
        if (!input.pendingMigrationsResolved) {
          toast.success(params.successToast ?? "Setup updated")
        }
        await store.dispatch(
          refreshInstallPanels({ appInstallationId: installId }),
        )
        if (input.pendingMigrationsResolved !== "denied") {
          close()
        }
      }}
      onManual={() => {
        consumeEntitySetupManualCallback()
        close()
      }}
    />
  )
}

export default EntitySetupSidebarHost
